import { blueGrey } from "@mui/material/colors"
import { createTheme } from "@mui/material/styles"
import { isMobile } from "react-device-detect"

const DARK_MODE_GREY = {
    50: "#F0F0F0",
    100: "#DCDCDC",
    200: "#C2C2C2",
    300: "#8f8f8f",
    400: "#6B6B6B",
    500: "#4C4B4C",
    600: "#313033",
    700: "#272629",
    800: "#1F1E1F",
    900: "#0E0D0E",
}

export const DISABLED_FIELD = {
    background: DARK_MODE_GREY[700],
    border: DARK_MODE_GREY[600],
    label: "#707070",
    value: "#888888",
}

const DarkTheme = createTheme({
    palette: {
        mode: "dark",
        text: {
            primary: "#FFFFFF",
            secondary: DARK_MODE_GREY[300],
            disabled: DISABLED_FIELD.value,
        },
        primary: {
            main: "#0B95BB",
            dark: "#007A98",
            light: "#0EB0DC",
            contrastText: "#FFFFFF",
        },
        secondary: blueGrey,
        success: {
            main: "#369B3B",
            light: "#4CAF50",
            dark: "#1B5E20",
            contrastText: "#FFFFFF",
        },
        info: {
            main: "#619CBA",
            light: "#6FC5F0",
            dark: "#37718E",
            contrastText: "#FFFFFF",
        },
        error: {
            main: "#F44336",
            light: "#E57373",
            dark: "#CA2F23",
            contrastText: "#FFFFFF",
        },
        warning: {
            main: "#F8AF41",
            light: "#FFE172",
            dark: "#C18003",
            contrastText: "#FFFFFF",
        },
        grey: DARK_MODE_GREY,
        background: {
            default: DARK_MODE_GREY[800],
            paper: DARK_MODE_GREY[700],
        },
        divider: DARK_MODE_GREY[600],
    },
    typography: {
        fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
        fontSize: 12,
        h1: {
            fontFamily: "'Manrope', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 400,
        },
        h2: {
            fontFamily: "'Manrope', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 400,
        },
        h3: {
            fontFamily: "'Manrope', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 400,
        },
        h4: {
            fontFamily: "'Manrope', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 400,
        },
        h5: {
            fontFamily: "'Manrope', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 400,
        },
        h6: {
            fontFamily: "'Manrope', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 400,
        },
        body1: {
            fontWeight: 400,
        },
        body2: {
            fontWeight: 400,
        },
        subtitle1: {
            fontWeight: 400,
        },
        subtitle2: {
            fontFamily: "'Manrope', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 400,
        },
        button: {
            fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 600,
            textTransform: "none",
        },
        caption: {
            color: DARK_MODE_GREY[300],
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                "*::-webkit-scrollbar": {
                    width: 8,
                    height: 8,
                },
                "*::-webkit-scrollbar-corner": {
                    backgroundColor: DARK_MODE_GREY[600],
                    color: DARK_MODE_GREY[600],
                },
                "*::-webkit-scrollbar-thumb": {
                    backgroundColor: DARK_MODE_GREY[600],
                    borderRadius: 4,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: DARK_MODE_GREY[900],
                },
            },
        },
        MuiButton: {
            defaultProps: {
                variant: "contained",
                size: isMobile ? "large" : "small",
            },
            styleOverrides: {
                root: {
                    textTransform: "none",
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
                    fontWeight: 600,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
                    fontWeight: 400,
                    "&.Mui-disabled": {
                        WebkitTextFillColor: DISABLED_FIELD.value,
                        color: DISABLED_FIELD.value,
                        opacity: 1,
                    },
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                size: "small",
            },
        },
        MuiIconButton: {
            defaultProps: {
                color: "default",
            },
            styleOverrides: {
                root: {
                    padding: 6,
                },
            },
        },
        MuiToggleButton: {
            defaultProps: {
                size: "small",
            },
        },
        MuiToggleButtonGroup: {
            defaultProps: {
                size: "small",
            },
            styleOverrides: {
                root: {
                    margin: 3,
                },
            },
        },
        MuiFormControl: {
            defaultProps: {
                variant: "filled",
                size: "small",
            },
            styleOverrides: {
                root: {
                    margin: 3,
                },
                fullWidth: {
                    width: "100%",
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    // to allow onClick events on TextField labels
                    pointerEvents: "auto",
                    width: "130%",
                    "&.Mui-disabled": {
                        color: DISABLED_FIELD.label,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    "&.Mui-disabled": {
                        color: DISABLED_FIELD.label,
                    },
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: "filled",
                size: "small",
                type: "number",
            },
        },
        MuiSelect: {
            defaultProps: {
                variant: "filled",
                size: "small",
            },
            styleOverrides: {
                icon: {
                    "&.Mui-disabled": {
                        color: DISABLED_FIELD.label,
                    },
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: DARK_MODE_GREY[600],
                    borderRadius: 2,
                    "&.Mui-disabled": {
                        backgroundColor: DISABLED_FIELD.background,
                        boxShadow: `0 0 0 1px ${DISABLED_FIELD.border}`,
                    },
                },
                underline: {
                    "&:before": {
                        borderBottom: "none",
                    },
                    "&:after": {
                        borderBottom: "none",
                    },
                    "&:hover:before": {
                        borderBottom: "none",
                    },
                    // removes the default dotted underline of a disabled field
                    "&.Mui-disabled:before": {
                        borderBottom: "none",
                        borderBottomStyle: "none",
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    "&.Mui-disabled": {
                        backgroundColor: DISABLED_FIELD.background,
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: DISABLED_FIELD.border,
                        },
                    },
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    backgroundColor: DARK_MODE_GREY[700],
                    backgroundImage: "none",
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: DARK_MODE_GREY[700],
                    backgroundImage: "none",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                },
            },
        },
    },
})

export default DarkTheme
